import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaSeedling, FaBolt, FaHandshake,
  FaStar, FaCheck, FaTimes,
  FaCalculator, FaUsers, FaInfinity,
} from 'react-icons/fa';

const plans = [
  {
    icon: FaSeedling,
    iconColor: 'text-green-500',
    name: 'Solo',
    badge: null,
    baseAnnual: 170,
    perEmpAnnual: 10,
    desc: 'For businesses that run one payroll per month.',
    highlight: false,
    features: [
      'One payroll run per month',
      // 'Direct deposit included',
      'Automated CPP, EI & income tax calculations',
      'Automated CRA remittances',
      'Employee self-serve portal (paystubs & T4s)',
      'Standard payroll reporting',
    ],
    notIncluded: [
      'Multiple payrolls per month',
      'Off-cycle & bonus pay runs',
      'Multiple pay groups / schedules',
    ],
    cta: 'Get Started',
  },
  {
    icon: FaBolt,
    iconColor: 'text-white',
    name: 'Unlimited',
    badge: 'Most Popular',
    baseAnnual: 270,
    perEmpAnnual: 10,
    desc: 'For teams that need frequent or flexible pay runs.',
    highlight: true,
    features: [
      'Unlimited payroll runs per month',
      'Multiple pay groups & schedules (weekly, bi-weekly, etc.)',
      'Off-cycle & bonus payroll runs',
      // 'Direct deposit included',
      'Automated CPP, EI & income tax calculations',
      'Automated CRA remittances',
      'Year-end T4 & T4A preparation and filing',
      // 'ROE submissions to Service Canada',
      'Employee self-serve portal',
      'Priority email & phone support',
    ],
    notIncluded: [],
    cta: 'Get Started',
  },
  {
    icon: FaHandshake,
    iconColor: 'text-payday-blue',
    name: 'Accounting Partners',
    badge: null,
    baseAnnual: null,
    desc: 'Discounted pricing for accountants managing multiple clients.',
    highlight: false,
    features: [
      'Partner-exclusive discounted rates',
      'Multi-client management dashboard',
      'Consolidated billing across all clients',
      'Collaborative access & custom roles',
      'Dedicated partner support channel',
      'White-label & co-branding options',
    ],
    notIncluded: [],
    cta: 'Contact Us',
  },
];

const faqs = [
  {
    q: 'How is the monthly payroll cost calculated?',
    a: 'Your monthly cost = Base fee + (number of active employees × per-employee fee). You only pay for employees who are actually paid in that billing cycle — no charges for inactive employees.',
  },
  {
    q: 'What counts as an "active employee"?',
    a: 'An active employee is any employee or contractor who receives at least one payment during your 30-day billing period. If someone is on leave and not paid, they are not counted.',
  },
  {
    q: 'What is the difference between Solo and Unlimited?',
    a: 'The Solo plan allows one payroll run per month — ideal for owner-operators or businesses with a single monthly payday. The Unlimited plan lets you run payroll as many times as you need, across multiple pay groups and schedules (weekly, bi-weekly, off-cycle bonuses, etc.).',
  },
  {
    q: 'Can I switch between Solo and Unlimited?',
    a: 'Yes! You can upgrade or downgrade your plan at any time. If your business grows or your payroll frequency changes, simply contact us and we will adjust your plan immediately.',
  },
  {
    q: 'Are there any setup fees or long-term contracts?',
    a: 'No. There are no setup fees, no onboarding costs, and no lock-in contracts. All plans are billed month-to-month and you can cancel at any time.',
  },
  {
    q: 'Are year-end T4 filings included?',
    a: 'Yes — T4 and T4A preparation, employee distribution, and CRA electronic filing are fully included in the Unlimited plan. Solo plan clients can add T4 filing as an optional add-on.',
  },
];

export default function Pricing() {
  const [employees, setEmployees] = useState(1);
  const [openFaq, setOpenFaq] = useState(null);

  React.useEffect(() => {
    document.title = 'OrientalPay | Simple & Transparent Pricing';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Straightforward Canadian payroll pricing with no hidden fees. Choose the Solo or Unlimited plan based on how often you run payroll.'
      );
    }
  }, []);

  const getSoloTotal = () => 170 + (employees > 1 ? (employees - 1) * 10 : 0);
  const getUnlimitedTotal = () => 270;

  return (
    <div className="min-h-screen bg-white">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img
          src="/PayrollHero.png"
          alt="Oriental Business Solutions Pricing"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1e45] via-[#0d1e45]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1e45]/80 via-transparent to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-20 pt-40 w-full">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-xs font-bold uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Simple Pricing
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] mb-6 max-w-3xl">
            Pay Only For<br />
            <span className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #ffbe1a, #ffd966)' }}>
              What You Use
            </span>
          </h1>
          <p className="text-blue-100 text-base md:text-lg font-medium leading-relaxed max-w-xl">
            Transparent Canadian payroll pricing — a small base fee plus a per-employee charge. No surprises.
          </p>
        </div>
      </section>

      {/* Cost Estimator */}
      <section className="py-12 px-6 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto">
          <div className="card-wagepoint p-8 section-pastel-blue border border-blue-150 flex flex-col md:flex-row items-center gap-8 shadow-sm">
            <div className="flex-1 w-full">
              <div className="flex items-center gap-2 text-payday-blue mb-4">
                <FaCalculator className="text-lg" />
                <span className="font-bold text-sm uppercase tracking-wider">Cost Estimator</span>
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">Estimate your annual cost</h2>
              <p className="text-gray-600 text-sm mb-6 font-medium">
                Drag the slider to see your estimated cost for both plans, billed annually.
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex justify-between text-sm font-bold text-gray-700">
                  <span className="flex items-center gap-1.5"><FaUsers className="text-payday-blue" /> Employees / Contractors</span>
                  <span className="text-payday-blue text-base bg-white px-3 py-1 rounded-lg border border-blue-100">{employees} people</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={employees}
                  onChange={(e) => setEmployees(parseInt(e.target.value))}
                  className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer accent-payday-blue"
                />
                <div className="flex justify-between text-xs text-gray-400 font-semibold">
                  <span>1</span>
                  <span>10+ (Unlimited Recommended)</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="w-full md:w-72 bg-white p-6 rounded-2xl border border-blue-100 flex flex-col gap-5 shadow-sm">
              <div>
                <div className="text-xs font-bold text-gray-400 mb-1 flex items-center gap-1">
                  <FaSeedling className="text-green-400" /> Solo Plan Estimate
                </div>
                <div className="text-2xl font-black text-gray-900">
                  C${getSoloTotal().toLocaleString('en-CA')}
                  <span className="text-xs font-medium text-gray-400">/year</span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {employees > 1
                    ? `C$170 base + C$10 × ${employees - 1} additional people`
                    : 'C$170 base price'}
                </div>
                <div className="text-xs text-green-600 font-bold mt-1">
                  (after 20% discount)
                </div>
              </div>
              <div className="border-t border-gray-100" />
              <div>
                <div className="text-xs font-bold text-payday-blue mb-1 flex items-center gap-1">
                  <FaInfinity className="text-payday-blue" /> Unlimited Plan Estimate
                </div>
                <div className="text-3xl font-black text-payday-blue">
                  C${getUnlimitedTotal().toLocaleString('en-CA')}
                  <span className="text-xs font-medium text-blue-400">/year</span>
                </div>
                <div className="text-xs text-blue-300 mt-0.5">Fixed annual price</div>
                <div className="text-xs text-green-600 font-bold mt-1">
                  (after 20% discount)
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 px-6 section-pastel-blue">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Choose your <span className="gradient-text">payroll plan</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto font-medium">
              Both plans include direct deposit, CRA remittances, and full employee portal access. Upgrade or downgrade at any time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {plans.map(({ icon: Icon, iconColor, name, badge, baseAnnual, perEmpAnnual, desc, highlight, features, notIncluded, cta }) => {
              return (
              <div
                key={name}
                className={`relative flex flex-col rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 bg-white border shadow-sm ${
                  highlight
                    ? 'border-payday-blue shadow-lg shadow-blue-900/5 ring-1 ring-payday-blue'
                    : 'border-gray-200'
                }`}
              >
                {badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1 btn-payday-blue text-xs font-bold shadow whitespace-nowrap flex items-center gap-1 rounded-full">
                    <FaStar className="text-pto-yellow text-[10px]" /> {badge}
                  </div>
                )}

                {/* Plan header */}
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${highlight ? 'bg-pastel-blue' : 'bg-gray-100'}`}>
                    <Icon className={`text-lg ${highlight ? 'text-payday-blue' : iconColor}`} />
                  </div>
                  <div>
                    <div className="font-extrabold text-lg text-gray-900">{name}</div>
                    <div className="text-xs text-gray-400 font-semibold">{desc}</div>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-5 pb-5 border-b border-gray-100">
                  {baseAnnual !== null ? (
                    <div>
                      <div className="flex items-end gap-1.5">
                        <span className="text-4xl font-black text-gray-900">C${baseAnnual}</span>
                        <span className="mb-1.5 text-sm text-gray-400">/yr base</span>
                        <span className="mb-1.5 text-xs text-gray-400 line-through">C${Math.round(baseAnnual / 0.8)}</span>
                      </div>
                      
                      <div className="text-xs text-green-600 font-bold mb-1">
                        ✔ Billed annually — after 20% deduction
                      </div>
                      
                      <div className="text-xs text-payday-blue font-bold mt-1">
                        + C${perEmpAnnual} per active employee / contractor
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-3xl font-black text-gray-900">Custom</div>
                      <div className="text-xs text-payday-blue font-bold mt-1">Partner-exclusive discount rates</div>
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="flex flex-col gap-2.5 flex-1 mb-6">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700 font-medium">
                      <FaCheck className="text-payday-blue text-xs flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {notIncluded.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-gray-300">
                      <FaTimes className="text-xs flex-shrink-0 text-gray-300 mt-0.5" />
                      <span className="line-through">{f}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact">
                  <button className={`w-full py-3 rounded-full text-sm font-bold transition-all duration-200 ${
                    highlight
                      ? 'btn-payday-blue shadow-md'
                      : 'border border-gray-200 text-gray-600 hover:border-payday-blue hover:text-payday-blue bg-white'
                  }`}>
                    {cta}
                  </button>
                </Link>
              </div>
              );
            })}
          </div>

          {/* Pricing note */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 text-center">
            <p className="text-gray-600 text-sm font-medium">
              All prices are in <strong>Canadian dollars (CAD)</strong> and exclude applicable taxes.
              You are only billed for employees who receive a payment in each billing cycle.
              {' '}<Link to="/contact" className="text-payday-blue font-bold hover:underline">Contact us</Link> if you have any questions.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black text-gray-900 text-center mb-10">
            Frequently <span className="gradient-text">asked questions</span>
          </h2>
          <div className="flex flex-col gap-3">
            {faqs.map(({ q, a }, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                <button
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-extrabold text-sm text-gray-800 pr-4">{q}</span>
                  <span className={`text-payday-blue flex-shrink-0 text-xs transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>▼</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 pt-2 text-sm text-gray-500 leading-relaxed border-t border-gray-100 font-medium">
                    {a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
